import { FC } from 'react';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { blue } from '@mui/material/colors';
import InsertDriveFileIconOutlined from '@mui/icons-material/InsertDriveFileOutlined';
import { Control, Controller } from 'react-hook-form';
import { downloadLinkAsFile } from 'further-ui/utils';
import { Row, Column } from 'further-ui/components/Layout';
import { UploadType } from 'further-types/files';
import FileDrop from 'further-ui/components/FileDrop';
import {
  getValidFileTypesForUploadType,
  getMaxSizeForUploadType,
} from 'further-ui/config';
import useFileUpload from 'hooks/ui/useFileUpload';
import { PeriodEndReportFormValues } from './schema';

const OuterColumn = styled(Column)({
  maxWidth: '800px',
});

const InnerColumn = styled(Column)({
  maxWidth: '600px',
});

const InsertDriveFileIcon = styled(InsertDriveFileIconOutlined)({
  fontSize: '16px',
});

const DownloadLink = styled(Typography)({
  color: blue[500],
  cursor: 'pointer',
  textDecoration: 'underline',
});

type Props = {
  control: Control<PeriodEndReportFormValues>;
  readOnly: boolean;
};

const PortfolioUpdateFile: FC<Props> = ({ control, readOnly }) => {
  const { uploadFile, loading } = useFileUpload();

  const handleDownload = (url: string, name: string) => {
    downloadLinkAsFile(url, name);
  };

  return (
    <OuterColumn spacing="sm">
      <InnerColumn spacing="xs">
        <Typography variant={readOnly ? 'h5' : 'h6'} color="grey.900">
          Portfolio update file
        </Typography>
        {readOnly ? null : (
          <Typography variant="body2">
            Upload a PDF file of your portfolio company updates. This file will
            be added to the report after the manager&apos;s commentary and
            before the fund financial statements. To support deliverability,
            keep any uploaded PDF under 5MB. The PDF upload size limit is 10MB.
          </Typography>
        )}
      </InnerColumn>
      <Controller
        control={control}
        name="portfolioUpdateFile"
        render={({
          field: { value, onChange },
          fieldState: { invalid, error },
        }) => {
          if (readOnly) {
            return (
              <Column spacing="xs">
                <Typography variant="h6" color="grey.600">
                  Attached file
                </Typography>
                <Row spacing="xs" centered>
                  <InsertDriveFileIcon />
                  <Typography variant="h6">{value?.name}</Typography>
                  <DownloadLink
                    variant="body2"
                    onClick={() => {
                      if (!value?.previewUrl || !value?.name) return;
                      handleDownload(value?.previewUrl, value?.name);
                    }}
                  >
                    Download
                  </DownloadLink>
                </Row>
              </Column>
            );
          }

          return (
            <FileDrop
              inputTitle="Upload file"
              height={120}
              maxFiles={1}
              error={invalid}
              helperText={error?.message}
              files={
                value
                  ? [
                      {
                        fileName: value.name,
                        isUploading: loading,
                      },
                    ]
                  : []
              }
              onDeleteFile={() => {
                onChange(null);
              }}
              onDropFile={async (files) => {
                if (files.length === 0) return;
                const file = files[0].fileData;
                onChange({
                  name: file.name,
                  path: '',
                  previewUrl: '',
                });

                const { data, error } = await uploadFile(
                  file,
                  UploadType.LpPortfolioCompanyUpdates,
                );

                if (error) {
                  onChange(null);
                  control.setError('portfolioUpdateFile', {
                    message: error.message,
                  });
                  return;
                }

                if (data) {
                  onChange({
                    name: file.name,
                    path: data.filePath,
                    previewUrl: data.url,
                  });
                }
              }}
              sizeRestriction={{
                unit: 'mb',
                value: getMaxSizeForUploadType(
                  UploadType.LpPortfolioCompanyUpdates,
                  'mb',
                ),
              }}
              supportedFormats={getValidFileTypesForUploadType(
                UploadType.LpPortfolioCompanyUpdates,
              )}
            />
          );
        }}
      />
    </OuterColumn>
  );
};

export default PortfolioUpdateFile;
